import { combineReducers } from 'redux'
import { IAction, IActionWithParams } from '../apiMiddleware'
import * as actions from './actions'
import { BOX_VIEWER_MODE, BoxViewerMode, IEmbedLinkParams } from './actions'

export interface IBoxViewerState {
  /**
   * What the viewer is showing: '' when closed, 'loading' while an embed link
   * is in flight, 'failed' when one errored, otherwise the embed URL.
   */
  link: string
  /**
   * Correlation id of the embed-link request whose response is still welcome,
   * or null when nothing is outstanding. See `nextEmbedRequestId` in actions.
   */
  pendingRequestId: number | null
  /** Why the viewer failed, when the server could say: a file Box no longer has. */
  failureMessage: string | null
}

const INITIAL_VIEWER_STATE: IBoxViewerState = {
  link: '',
  pendingRequestId: null,
  failureMessage: null,
}

/**
 * Whether a completed embed-link request is still the one the viewer is waiting
 * for. Anything else is a straggler: a request the user has since retargeted
 * away from, or one that outlived the panel being closed.
 */
const isLatestEmbedRequest = (
  state: IBoxViewerState,
  action: IActionWithParams<IEmbedLinkParams>,
) => action.params?.requestId === state.pendingRequestId

export default combineReducers({
  // The link and the id of the request allowed to change it live in one slice
  // because `combineReducers` gives a reducer no view of its siblings, and the
  // two are only meaningful together. The `boxViewLink` selector still hands
  // the rest of the codebase a plain string.
  viewer(
    state: IBoxViewerState = INITIAL_VIEWER_STATE,
    action: IActionWithParams<IEmbedLinkParams>,
  ): IBoxViewerState {
    switch (action.type) {
      case actions.SET_BOX_VIEWER_LINK:
        // Any explicit set supersedes whatever was in flight — including the
        // empty-link set that closes the panel. Without dropping the pending id
        // here, a slow response arriving after the user hit Escape would write
        // its URL back and reopen the panel they just dismissed.
        return {
          link: (action.data as { link: string; mode: BoxViewerMode })?.link ?? '',
          pendingRequestId: null,
          failureMessage: null,
        }
      case actions.GET_EMBED_LINK_REQUEST:
        return {
          link: 'loading',
          pendingRequestId: action.params?.requestId ?? null,
          failureMessage: null,
        }
      case actions.GET_EMBED_LINK_SUCCESS:
        if (!isLatestEmbedRequest(state, action)) {
          return state
        }
        return { link: action.data as string, pendingRequestId: null, failureMessage: null }
      case actions.GET_EMBED_LINK_FAILURE:
        if (!isLatestEmbedRequest(state, action)) {
          return state
        }
        return {
          link: 'failed',
          pendingRequestId: null,
          failureMessage: action.error?.code === 'NOT_FOUND' ? action.error.message : null,
        }
      case actions.HIDE_BOX_PREVIEW:
        return INITIAL_VIEWER_STATE
      default:
        return state
    }
  },
  // Display mode for the BoxViewer surface. `drawer` is the original
  // right-edge popout used everywhere; `modal` is a centered overlay used
  // by chat-message attachment chips. Reset only on close — a fetch failure
  // must keep the mode so the error state renders in whichever surface the
  // user opened (modal users shouldn't see the failure pop in as a drawer).
  boxViewerMode(state: BoxViewerMode = BOX_VIEWER_MODE.DRAWER, action: IAction): BoxViewerMode {
    switch (action.type) {
      case actions.SET_BOX_VIEWER_LINK:
      case actions.SET_BOX_VIEWER_MODE:
        return (
          (action.data as { link: string; mode: BoxViewerMode })?.mode ?? BOX_VIEWER_MODE.DRAWER
        )
      case actions.HIDE_BOX_PREVIEW:
        return BOX_VIEWER_MODE.DRAWER
      default:
        return state
    }
  },
  // Set when the user expands the drawer into the modal, so only that modal offers to
  // collapse back. Any new open or close clears it.
  isBoxViewerExpanded(state = false, action: IAction): boolean {
    switch (action.type) {
      case actions.SET_BOX_VIEWER_MODE:
        return (action.data as { mode: BoxViewerMode })?.mode === BOX_VIEWER_MODE.MODAL
      case actions.SET_BOX_VIEWER_LINK:
      case actions.HIDE_BOX_PREVIEW:
        return false
      default:
        return state
    }
  },
  // Box file id of whatever's currently open in the viewer, when known.
  // Optional — set by callers that opened the viewer for a specific known
  // file (e.g. SmartLink / chat-attachment chips). Drives the "Open in Box"
  // shortcut in the modal header.
  boxViewerFileId(state: string | null = null, action: IAction): string | null {
    switch (action.type) {
      case actions.SET_BOX_VIEWER_LINK:
        return (action.data as { boxFileId?: string })?.boxFileId ?? null
      case actions.HIDE_BOX_PREVIEW:
        return null
      default:
        return state
    }
  },
})
